<template>
    <div>
        <div class="radiobuttons">
            <radio-buttons
                list-heading="App.Bittimittari.LeftSlot.SelectTopic"
                :radio-button-list="radioButtonListSubjects"
                :default-radio-button="selectedSubjectRadioButton.labelText"
                :sub-topic="subTopic"
                :select-by-url="true"
                @selected-radio-button="handleSelectedRadioButton"
                @radio-buttons-changed="handleRadioButtonsChanged"
            />
        </div>
        <div
            v-show="showRadiobuttonsTimeOfDay"
            class="radiobuttons"
        >
            <radio-buttons
                :key="forceUpdateKey"
                list-heading="App.RadioButtonListHeader.Bittimittari.TimeOfDay"
                :radio-button-list="radioButtonListTimeOfDay"
                :whitelist-subjects="false"
                @selected-radio-button="handleSelectedRadioButtonTimeOfDay"
            />
        </div>
        <div
            v-show="showRadiobuttonsOperators"
            class="radiobuttons"
        >
            <div class="radiobuttons">
                <radio-buttons
                    :key="forceUpdateKey"
                    list-heading="App.RadioButtonListHeader.Bittimittari.OperatorsList"
                    :radio-button-list="radioButtonListOperators"
                    :whitelist-subjects="false"
                    @selected-radio-button="handleSelectedRadioButtonOperators"
                />
            </div>
            <radio-buttons
                :key="forceUpdateKey"
                list-heading="App.RadioButtonListHeader.Bittimittari.DataTransferDirection"
                :radio-button-list="radioButtonListDataTransferDirection"
                :default-radio-button="radioButtonListDataTransferDirection[0].labelText"
                :whitelist-subjects="false"
                @selected-radio-button="handleSelectedRadioButtonDataTransferDirection"
            />
        </div>
        <div v-show="showAreaSelection">
            <!--<guide
                id="area-selection-guide"
                :guide-text="guideText"
            />-->
            <area-lists
                :key="forceUpdateKey"
                list-heading="App.RadioButtonListHeader.ValitseAlue"
                :visible-area-types="visibleAreaTypes"
                :selected-area-type="selectedAreaType"
                @selected-radio-button="handleAreaSelectionRadioButton"
                @selected-list-value="handleAreaSelectionListValue"
            />
        </div>
        <div
            v-show="locateMeVisible"
            class="search-components"
        >
            <search-address />
            <locate-me />
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Vue, Watch, Prop } from "vue-facing-decorator"
    import SearchAddress from "@/components/SearchAddress.vue"
    import LocateMeLink from "@/components/LocateMeLink.vue"
    import RadioButtonListWithDropdown from "@/components/RadioButtonListWithDropdown.vue"
    import RadioButtonList from "@/components/RadioButtonList.vue"
    import RadioButtonModel from "@/models/radiobuttonmodel"
    import SimpleRadioButtonModel from "@/models/simpleradiobuttonmodel"
    import MunicipalitiesAndProvinces from "@/components/MunicipalitiesAndProvinces.vue"
    import BittimittariService from "@/services/BittimittariService"
    import SearchResultModel from "@/models/searchresultmodel"
    import Guide from "@/components/Guide.vue"
    import { TimeOfDay, AreaTypes } from "@/helpers/enums"
    import { store } from '@/store/store'

    @Component({
        components: {
            "radio-buttons": RadioButtonListWithDropdown,
            "radio-button-list": RadioButtonList,
            "locate-me": LocateMeLink,
            "search-address": SearchAddress,
            "area-lists": MunicipalitiesAndProvinces,
            Guide
        }
    })
    export default class Bittimittari extends Vue {
        @Prop({ type: String }) subTopic!: string

        // DataPublishServices rajapintakutsun etuosa
        apiCallBasePart: string = "v1/Bittimittari/"
        pageName: string = "bittimittari"
        BittimittariService: BittimittariService = new BittimittariService()

        radioButtonListSubjects: Array<RadioButtonModel> = this.radioButtonListSubjectsValues
        operatorList: SimpleRadioButtonModel[] = []
        visibleAreaTypes: Array<AreaTypes> = [AreaTypes.Municipality, AreaTypes.Province, AreaTypes.Country]

        selectedSubjectRadioButton: RadioButtonModel = this.defaultRadioButton
        selectedTimeOfDayRadioButton: RadioButtonModel | undefined = undefined
        selectedOperatorRadioButton: SimpleRadioButtonModel | undefined = undefined
        selectedDataTransferDirectionRadioButton: RadioButtonModel = this.radioButtonListDataTransferDirection[0]
        selectedAreaType: AreaTypes = this.defaultRadioButton.radioValue === "BittimittariVuorokaudenaikaanPohjautuvatTiedot" ? AreaTypes.Country : AreaTypes.Municipality

        bittimittariLocationsSearchVisible: boolean = true
        forceUpdateKey = 0
        guideText: string = ""

        mounted(): void {
            store.dispatch("setSelectedSubjectRadioButton", this.selectedSubjectRadioButton)
            this.initRadioButtonListOperators()
        }

        async activated(): Promise<void> {
            // Resetoidaan radiobuttoneiden valinnat ja pakotetaan komponentit päivittymään
            this.selectedTimeOfDayRadioButton = undefined
            this.selectedOperatorRadioButton = undefined
            this.selectedDataTransferDirectionRadioButton = this.radioButtonListDataTransferDirection[0]
            this.selectedAreaType = AreaTypes.Country
            this.forceUpdateKey += 1

            // tyhjennetään kartan edellinen lokaatiot
            store.dispatch("setStopLocate")
            store.dispatch("setCurrentPage", this.pageName)
            store.dispatch("setLoading", { name: this.pageName, value: true })
            this.radioButtonListSubjects = this.radioButtonListSubjectsValues
            this.selectedSubjectRadioButton = this.defaultRadioButton
            store.dispatch("setSelectedSubjectRadioButton", this.selectedSubjectRadioButton)

            // Asetetaan oikean slotin sisältö
            const dataType = this.selectedSubjectRadioButton.radioValue
            store.dispatch("setRightSlot", dataType)

            this.$nextTick(async () => {
                // Annetaan kartan latautua ja tehdään sitten apikutsu, jolla haetaan kartan data
                await this.setDataToLoad(this.selectedSubjectRadioButton)
            })

            store.dispatch("setLoading", { name: this.pageName, value: false })
        }

        deactivated() {
            this.radioButtonListSubjects = []
            store.dispatch("setSubTopic", null)
            store.dispatch("setCurrentPage", "")
            store.dispatch("setSelectedAreaRadioButton", "")
            store.dispatch("setSelectedMunicipality", "")
            store.dispatch("setSelectedProvince", "")
            store.dispatch("setInfokarttaRadioValues", [])
            store.dispatch("setSelectedDropDownResourceKey", "")
        }

        get leftSlotHeaderBittimittari(): string | undefined {
            return this.$t("App.LeftSlot.Header.Bittimittari")
        }

        get leftSlotInfoText(): string | undefined {
            return this.$t("App.Bittimittari.LeftSlot.InfoText")
        }

        get radioButtonListSubjectsValues(): Array<RadioButtonModel> {
            return [new RadioButtonModel("App.RadioList.TimeOfDay", "BittimittariVuorokaudenaikaanPohjautuvatTiedot"),
                    new RadioButtonModel("App.RadioList.OperaattorikohtaisetTiedot", "BittimittariOperaattorikohtaisetTiedot"),
                    new RadioButtonModel("App.RadioList.VuorokaudenaikaanPohjautuvatRuudut", "BittimittariVuorokaudenaikaanPohjautuvatRuudut")
            ]
        }

        get showRadiobuttonsTimeOfDay(): boolean {
            return this.selectedSubjectRadioButton.radioValue === "BittimittariVuorokaudenaikaanPohjautuvatTiedot"
                || this.selectedSubjectRadioButton.radioValue === "BittimittariVuorokaudenaikaanPohjautuvatRuudut"
        }

        get showRadiobuttonsOperators(): boolean {
            return this.selectedSubjectRadioButton.radioValue === "BittimittariOperaattorikohtaisetTiedot"
        }

        get showAreaSelection(): boolean {
            return this.selectedSubjectRadioButton.radioValue === "BittimittariVuorokaudenaikaanPohjautuvatTiedot"
                || this.selectedSubjectRadioButton.radioValue === "BittimittariOperaattorikohtaisetTiedot"
        }

        get locateMeVisible(): boolean {
            return this.selectedSubjectRadioButton.radioValue === "BittimittariVuorokaudenaikaanPohjautuvatRuudut"
        }

        get activeLang(): SearchResultModel {
            return store.getters.activeLang
        }

        @Watch("activeLang")
        async onActiveLang(): Promise<void> {
            // kielivalinta muuttunut. nyt pitäisi lataa data uudestaan ja käyttäjän tekemä mahdollinen valinta....
            if (this.currentPage !== this.pageName) {
                return
            }
            this.updateData(store.getters.rightSlot)
            this.updateGuideText()
        }

        get subjectChanged(): RadioButtonModel {
            return this.selectedSubjectRadioButton
        }

        @Watch("subjectChanged")
        async onSubjectChanged(): Promise<void> {
            // asetetaan aluevalinnat sen mukaan, mikä aihe on valittuna
            this.setVisibleAreaTypes()
            this.updateGuideText()
        }

        get defaultRadioButton(): RadioButtonModel {
            // Jos sivulle tullaan subTopic routen kautta, niin asetetaan default radiobutton value routen mukaisesti
            if (this.subTopic) {
                for (let i = 0; i < this.radioButtonListSubjects.length; i++) {
                    if (this.radioButtonListSubjects[i].radioValue === this.subTopic) {
                        return this.radioButtonListSubjects[i]
                    }
                }
            }
            return this.radioButtonListSubjects[0]
        }

        // Aihe valittu
        async handleSelectedRadioButton(item: RadioButtonModel) {
            this.selectedSubjectRadioButton = item
            store.dispatch("setSelectedSubjectRadioButton", item)
            this.setVisibleAreaTypes()
            this.updateGuideText()

            if (item.radioValue === "BittimittariOperaattorikohtaisetTiedot") {
                // Asetetaan valittu operaattori takaisin rightslotin otsikkoon
                this.$store.dispatch("setSelectedDropDownResourceKey", this.selectedOperatorRadioButton?.labelText)
            }
            // Tehdään apikutsu, jolla haetaan kartan data
            await this.setDataToLoad(this.selectedSubjectRadioButton)
        }

        get radioButtonListTimeOfDay(): Array<RadioButtonModel> {
            return [new RadioButtonModel("App.Bittimittari.RadioList.Night", "Night", "BittimittariVuorokaudenaikaanPohjautuvatTiedot"),
                    new RadioButtonModel("App.Bittimittari.RadioList.Daytime", "Daytime", "BittimittariVuorokaudenaikaanPohjautuvatTiedot"),
                    new RadioButtonModel("App.Bittimittari.RadioList.Evening", "Evening", "BittimittariVuorokaudenaikaanPohjautuvatTiedot")
            ]
        }

        get radioButtonListOperators(): Array<SimpleRadioButtonModel> {
            return this.operatorList
        }

        initRadioButtonListOperators() {
            const operatorRadioList: SimpleRadioButtonModel[] = []
            for (const operator of store.getters.operatorList) {
                const operatorItem = new SimpleRadioButtonModel(operator.name, operator.name, "BittimittariOperaattorikohtaisetTiedot", operator.id)
                operatorRadioList.push(operatorItem)
            }
            this.operatorList = operatorRadioList
        }

        get radioButtonListDataTransferDirection(): Array<RadioButtonModel> {
            return [new RadioButtonModel("App.Bittimittari.RadioList.Download", "Download", "BittimittariOperaattorikohtaisetTiedot"),
                    new RadioButtonModel("App.Bittimittari.RadioList.Upload", "Upload", "BittimittariOperaattorikohtaisetTiedot")
            ]
        }

        get currentPage(): string {
            return store.getters.currentPage
        }

        // Vuorokaudenaika valittu
        async handleSelectedRadioButtonTimeOfDay(item: RadioButtonModel) {
            this.selectedTimeOfDayRadioButton = item
            const selectedTimeOfDay = this.selectedTimeOfDayRadioButton.radioValue
            const timeOfDayEnum = TimeOfDay[selectedTimeOfDay]
            store.dispatch("setTimeOfDay", timeOfDayEnum)
            await this.updateData(store.getters.rightSlot)
        }

        // Operaattori valittu
        async handleSelectedRadioButtonOperators(item: SimpleRadioButtonModel) {
            // päivittää valittu operaattori sen id:n mukaan storeen mukaisesti
            store.dispatch("setOperatorId", item.id)

            // Asetetaan valittu operaattori rightslotin otsikkoon
            this.$store.dispatch("setSelectedDropDownResourceKey", item.labelText)

            // operaattorin valinta
            this.selectedOperatorRadioButton = item
            await this.updateData(store.getters.rightSlot)
        }

        // Tiedonsiirron suunta valittu
        async handleSelectedRadioButtonDataTransferDirection(item: RadioButtonModel) {
            this.selectedDataTransferDirectionRadioButton = item
            store.dispatch("setDataTransferDirection", item.radioValue)
            await this.updateData(store.getters.rightSlot)
        }

        updateGuideText(): void {
            let key = ""
            if (this.$store.getters.selectedSubjectRadioButton) {
                // Tekstiresurssi haetaan radiobuttonin perusteella
                key = "App." + this.$store.getters.selectedSubjectRadioButton.radioValue + ".GuideText"
            }
            const guideText = this.$t(key)
            if (guideText === key) {
                // Tekstiresurssia ei löytynyt
                this.guideText = ""
            }
            else {
                this.guideText = guideText !== undefined ? guideText : ""
            }
        }

        getAreaStringValue(index: number): string {
            // TODO/LLa kun ruudut lisätään alueisiin, indexit muuttuvat
            switch (index) {
                case 0:
                    return "kunta"
                case 1:
                    return "maakunta"
                case 2:
                    return "kokosuomi"
                default:
                    return "kunta"
            }
        }

        handleAreaSelectionRadioButton(item: RadioButtonModel) {
            store.dispatch("setSelectedAreaRadioButton", String(item))
            this.updateData(store.getters.rightSlot)
        }

        handleAreaSelectionListValue(item: string) {
            // Valittu kunta tai maakunta dropdownista
            if (store.getters.selectedAreaRadioButton === "kunta") {
                store.dispatch("setSelectedMunicipality", item)
            }
            else if (store.getters.selectedAreaRadioButton === "maakunta") {
                store.dispatch("setSelectedProvince", item)
            }
        }

        handleRadioButtonsChanged(list: Array<RadioButtonModel>) {
            // Päivitä defaultina valittu radio button
            this.selectedSubjectRadioButton = list[0]
        }

        get defaultArea(): AreaTypes {
            if (this.selectedSubjectRadioButton.radioValue === "BittimittariVuorokaudenaikaanPohjautuvatTiedot") {
                return AreaTypes.Country
            }
            else if (this.selectedSubjectRadioButton.radioValue === "BittimittariOperaattorikohtaisetTiedot") {
                return AreaTypes.Municipality
            }
            return AreaTypes.Municipality
        }

        setSelectedAreaType(): AreaTypes {
            const selected = this.defaultRadioButton
            if (selected.radioValue === "BittimittariVuorokaudenaikaanPohjautuvatTiedot") {
                return AreaTypes.Country
            }
            else if (selected.radioValue === "BittimittariOperaattorikohtaisetTiedot") {
                return AreaTypes.Province
            }
            return AreaTypes.Province
        }

        async setVisibleAreaTypes() {
            if (this.selectedSubjectRadioButton.radioValue === "BittimittariVuorokaudenaikaanPohjautuvatTiedot") {
                this.visibleAreaTypes = [AreaTypes.Municipality, AreaTypes.Province, AreaTypes.Country]
            }
            else if (this.selectedSubjectRadioButton.radioValue === "BittimittariOperaattorikohtaisetTiedot") {
                this.visibleAreaTypes = [AreaTypes.Municipality, AreaTypes.Province]
                // Tarkistetaan onko valittu alue sellainen, joka ei näy operaattorisivulla ja jos on, päivitetään se maakunta -default arvoon
                if (store.getters.selectedAreaRadioButton === "kokosuomi") {
                    // Joudutaan pakottamaan selectedAreaTypen päivitys muuttamalla se väliaikaisesti muuksi kuin maakunnaksi,
                    // jotta tieto menee RadioButtonListWithDropdownSearch - komponentille asti.
                    // muutois selected ei muutu ja vaikka muu data päivittyy, radio buttonin valittu arvo ei näy visuaalisesti
                    this.selectedAreaType = AreaTypes.Municipality
                    this.$nextTick(async () => {
                        const areaString = this.getAreaStringValue(AreaTypes.Municipality)
                        store.dispatch("setSelectedAreaRadioButton", areaString)

                        this.selectedAreaType = AreaTypes.Municipality

                        await this.updateData(store.getters.rightSlot)
                    })
                }
            }
        }

        async setDataToLoad(selectedRadio: RadioButtonModel): Promise<void> {
            if (selectedRadio !== this.selectedSubjectRadioButton) {
                return
            }

            if (this.currentPage !== this.pageName) {
                return
            }

            this.updateData(selectedRadio.radioValue)
        }

        async updateData(radioValue: string): Promise<void> {
            store.dispatch("setLoading", { name: "bittimittari", value: true })
            const dataType = this.selectedSubjectRadioButton.radioValue

            // Asetetaan oikean slotin sisältö
            store.dispatch("setRightSlot", dataType)

            const clearLocationMarker = store.getters.currentPage.toLowerCase() !== store.getters.dataUrlToLoad?.url?.split("/")[1]?.toLowerCase()

            if (radioValue === "BittimittariVuorokaudenaikaanPohjautuvatTiedot") {
                const filters = new URLSearchParams()

                // Jos vuorokaudenaikaa ei ole valittu niin haetaan data silti, jotta kartta päivittyy
                if (this.selectedTimeOfDayRadioButton !== undefined) {
                    const selectedTimeOfDay = this.selectedTimeOfDayRadioButton.radioValue
                    const timeOfDayEnum = TimeOfDay[selectedTimeOfDay]
                    filters.append("vuorokaudenAikaId", timeOfDayEnum)
                }

                store.dispatch("setRightSlot", "BittimittariVuorokaudenaikaanPohjautuvatTiedot")
                const selectedAreaRadioButton = store.getters.selectedAreaRadioButton
                if (selectedAreaRadioButton === "kunta") {
                    store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart + "VuorokaudenAikaKlusteritKuntaKohtainen", type: dataType, params: filters, clearLocationMarker })
                }
                else if (selectedAreaRadioButton === "maakunta") {
                    store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart + "VuorokaudenAikaKlusteritMaakuntaKohtainen", type: dataType, params: filters, clearLocationMarker })
                }
                else {
                    store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart + "VuorokaudenAikaKlusteritKokoSuomi", type: dataType, params: filters, clearLocationMarker })
                }
            }
            else if (radioValue === "BittimittariOperaattorikohtaisetTiedot") {
                // Valittu operaattori. Vaikka operaattoria ei ole valittu, haetaan data silti, jotta kartta päivittyy
                const selectedOperatorId = this.selectedOperatorRadioButton?.id?.toString() ?? "0"

                const filters = new URLSearchParams()
                if (selectedOperatorId) {
                    filters.append("operaattoriId", selectedOperatorId)

                    const selectedAreaRadioButton = store.getters.selectedAreaRadioButton
                    if (selectedAreaRadioButton === "kunta") {
                        store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart + "VerkkoOperaattoriKlusteritKuntaKohtainen", type: dataType, params: filters, clearLocationMarker })
                    }
                    else if (selectedAreaRadioButton === "maakunta") {
                        store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart + "VerkkoOperaattoriKlusteritMaakuntaKohtainen", type: dataType, params: filters, clearLocationMarker })
                    }
                }
            }
            else if (radioValue === "BittimittariVuorokaudenaikaanPohjautuvatRuudut") {
                const filters = new URLSearchParams()

                // Jos vuorokaudenaikaa ei ole valittu niin haetaan data silti, jotta kartta päivittyy
                if (this.selectedTimeOfDayRadioButton !== undefined) {
                    const selectedTimeOfDay = this.selectedTimeOfDayRadioButton.radioValue
                    const timeOfDayEnum = TimeOfDay[selectedTimeOfDay]
                    filters.append("vuorokaudenAikaId", timeOfDayEnum)
                }

                store.dispatch("setRightSlot", "BittimittariVuorokaudenaikaanPohjautuvatRuudut")

                store.dispatch("setDataUrlToLoad", { url: this.apiCallBasePart + "RuutuKlusteritVuorokaudenAika", type: dataType, params: filters, clearLocationMarker })
            }

            store.dispatch("setLoading", { name: "bittimittari", value: false })
        }
    }
</script>

<style scoped>
    .radiobuttons {
        margin-bottom: 1.25rem;
    }

    #area-selection-guide {
        margin: 0rem -1.25rem;
        padding: 1.25rem;
        border-top: 5px solid #F4F6F9;
    }
</style>
